import { MissCache } from '../utils/missCache';
import { forecastWindow, localDateKey } from '../utils/travelState';

const GOOGLE_MAPS_API_KEY = (import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY;

export interface WeatherData {
  temp: number;
  condition: string;
  emoji: string;
  isForecast: boolean;
}

// ── Module-level caches (persist for the lifetime of the page) ───────────────
// MissCache, not Map: these all store `null` for "no answer", and a plain Map
// kept that null for the life of the page. One offline second, one rate-limit
// blip, one transient REQUEST_DENIED, and that destination had no weather until
// a reload. A genuine "nowhere like that" is still worth remembering, so a null
// is kept briefly rather than not at all.
const geocodeCache = new MissCache<{ lat: number; lng: number }>();
const climateCache = new MissCache<number>();
// Forecast cache keyed by "lat,lng,date" so navigating across days in the same
// city doesn't hammer Open-Meteo and trigger silent rate-limit failures.
const forecastCache = new MissCache<WeatherData>();
const forecastRangeCache = new MissCache<{ min: number; max: number }>();

// Fallback geocoder — Open-Meteo's own free geocoding API, no key required.
// Tried only when Google's geocode comes back empty, so a Google-side problem
// specific to this deployment (key restrictions, quota, a transient REQUEST_DENIED)
// doesn't sink weather for an otherwise perfectly real, findable city — this is a
// different index/service entirely, not a retry of the same lookup. Global coverage
// (not a fixed city list), so this isn't "fuzzy-matching a small table" either.
async function getGeocodingFromOpenMeteo(city: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const response = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=1&language=en&format=json`
    );
    if (!response.ok) return null;
    const data = await response.json();
    const first = data.results?.[0];
    if (first && typeof first.latitude === 'number' && typeof first.longitude === 'number') {
      return { lat: first.latitude, lng: first.longitude };
    }
    return null;
  } catch (error) {
    console.warn('[weather] Open-Meteo geocoding fallback failed', error);
    return null;
  }
}

export async function getGeocoding(city: string): Promise<{ lat: number; lng: number } | null> {
  const cached = geocodeCache.get(city);
  if (cached !== undefined) return cached;

  const tryGoogle = async (): Promise<{ lat: number; lng: number } | null> => {
    if (!GOOGLE_MAPS_API_KEY) {
      console.warn("VITE_GOOGLE_MAPS_API_KEY is missing. Geocoding will not work.");
      return null;
    }
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(city)}&key=${GOOGLE_MAPS_API_KEY}`
      );
      if (!response.ok) {
         console.error("Geocoding API response not OK:", response.status);
         return null;
      }
      const data = await response.json();
      if (data.status === "OK" && data.results.length > 0) {
        return data.results[0].geometry.location as { lat: number; lng: number };
      } else if (data.status === "REQUEST_DENIED") {
        console.error("Geocoding API request denied. Check your API key and permissions.");
      } else {
        console.warn("Geocoding failed for city:", city, "Status:", data.status);
      }
      return null;
    } catch (error) {
      console.error("Geocoding network error:", error);
      return null;
    }
  };

  const coords = (await tryGoogle()) ?? (await getGeocodingFromOpenMeteo(city));
  geocodeCache.set(city, coords);
  return coords;
}

const weatherCodeToEmoji: Record<number, string> = {
  0:  "☀️",  // clear
  1:  "🌤️", // mostly clear
  2:  "⛅",  // partly cloudy
  3:  "☁️",  // overcast
  45: "☁️",  // fog
  48: "☁️",  // depositing rime fog
  51: "🌦️", // drizzle
  53: "🌦️", // drizzle
  55: "🌦️", // drizzle
  56: "🌧️", // freezing drizzle
  57: "🌧️", // freezing drizzle
  61: "🌧️", // rain
  63: "🌧️", // rain
  65: "🌧️", // rain
  71: "❄️",  // snow
  73: "❄️",  // snow
  75: "❄️",  // snow
  77: "❄️",  // snow grains
  80: "🌧️", // showers
  81: "🌧️", // showers
  82: "🌧️", // showers
  85: "❄️",  // snow showers
  86: "❄️",  // snow showers
  95: "🌩️", // thunderstorm
  96: "🌩️", // thunderstorm with hail
  99: "🌩️", // thunderstorm with hail
};

export async function getForecast(lat: number, lng: number, date: string): Promise<WeatherData | null> {
  // Open-Meteo forecast API supports up to 16 days ahead. Skip dates outside
  // that window to avoid noisy 400 errors in the console.
  // Calendar dates: local midnight vs local noon counted half a day high,
  // dropping day 16 and letting yesterday through.
  if (!forecastWindow(date, date, localDateKey())) return null;

  const cacheKey = `${lat.toFixed(2)},${lng.toFixed(2)},${date}`;
  const cached = forecastCache.get(cacheKey);
  if (cached !== undefined) return cached;

  try {
    // No model specified → Open-Meteo uses best_match, which picks the most
    // appropriate model for the date range. ECMWF IFS is only valid ~10 days;
    // specifying it for day 11-16 returns 0 rather than null, causing "0 °C".
    const response = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,weather_code&start_date=${date}&end_date=${date}&timezone=auto`
    );
    if (!response.ok) {
      console.warn(`[weather] forecast API returned ${response.status} for ${date}`);
      forecastCache.set(cacheKey, null);
      return null;
    }
    const data = await response.json();

    const temps: number[] | undefined = data.daily?.temperature_2m_max;
    const rawTemp = temps?.[0];
    if (rawTemp !== null && rawTemp !== undefined) {
      const temp = Math.round(rawTemp);
      // Accept either current (weather_code) or legacy (weathercode) field name.
      const code: number = (data.daily?.weather_code ?? data.daily?.weathercode)?.[0] ?? 0;
      const result: WeatherData = {
        temp,
        condition: "",
        emoji: weatherCodeToEmoji[code] || "🌤️",
        isForecast: true,
      };
      forecastCache.set(cacheKey, result);
      return result;
    }
    forecastCache.set(cacheKey, null);
    return null;
  } catch (error) {
    console.warn('[weather] forecast fetch failed', error);
    forecastCache.set(cacheKey, null);
    return null;
  }
}

/**
 * Fetches the spread of daily maximum temperatures (min..max) forecast across
 * a date range — used for a destination-level "Forecast 20°C - 25°C" summary
 * rather than a single day's reading. Clamped to Open-Meteo's forecast window
 * (today .. +16 days); returns null if the range falls entirely outside it or
 * no days in range have forecast data, so the caller can fall back to the
 * climate-average "Usually X°C" display.
 */
export async function getForecastRange(lat: number, lng: number, startDate: string, endDate: string): Promise<{ min: number; max: number } | null> {
  const window = forecastWindow(startDate, endDate, localDateKey());
  if (!window) return null;
  const { start: rangeStart, end: rangeEnd } = window;

  const cacheKey = `${lat.toFixed(2)},${lng.toFixed(2)},${rangeStart},${rangeEnd}`;
  const cached = forecastRangeCache.get(cacheKey);
  if (cached !== undefined) return cached;

  try {
    const response = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max&start_date=${rangeStart}&end_date=${rangeEnd}&timezone=auto`
    );
    if (!response.ok) {
      console.warn(`[weather] forecast range API returned ${response.status} for ${rangeStart}..${rangeEnd}`);
      forecastRangeCache.set(cacheKey, null);
      return null;
    }
    const data = await response.json();
    const temps: number[] = (data.daily?.temperature_2m_max ?? []).filter((t: unknown) => t !== null && t !== undefined) as number[];
    if (temps.length === 0) {
      forecastRangeCache.set(cacheKey, null);
      return null;
    }
    const result = { min: Math.round(Math.min(...temps)), max: Math.round(Math.max(...temps)) };
    forecastRangeCache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.warn('[weather] forecast range fetch failed', error);
    forecastRangeCache.set(cacheKey, null);
    return null;
  }
}

/**
 * Fetches the average daily maximum temperature for a given month and location
 * using the Open-Meteo ERA5 historical archive (free, no key needed).
 * Averages the last 5 complete years so the result is a proper climatic normal
 * rather than a single-year reading. Results are cached per city+month.
 *
 * @param lat   Latitude
 * @param lng   Longitude
 * @param month 1-based month number (1 = Jan … 12 = Dec)
 */
async function fetchYearMonthAvg(lat: number, lng: number, year: number, month: number): Promise<number | null> {
  const mm = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  const startDate = `${year}-${mm}-01`;
  const endDate   = `${year}-${mm}-${String(lastDay).padStart(2, '0')}`;
  const res = await fetch(
    `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}` +
    `&start_date=${startDate}&end_date=${endDate}&daily=temperature_2m_max&timezone=auto`
  );
  if (!res.ok) {
    console.warn(`[weather] archive API returned ${res.status} for ${year}-${mm}`);
    return null;
  }
  const data = await res.json();
  const temps: number[] = (data.daily?.temperature_2m_max ?? []).filter((t: unknown) => t !== null && t !== undefined) as number[];
  return temps.length > 0
    ? Math.round(temps.reduce((a, b) => a + b, 0) / temps.length)
    : null;
}

export async function getClimateAvg(lat: number, lng: number, month: number): Promise<number | null> {
  const key = `${lat.toFixed(2)},${lng.toFixed(2)},${month}`;
  const cached = climateCache.get(key);
  if (cached !== undefined) return cached;

  // Average the last 5 complete years for a robust climatic normal.
  const refYear = new Date().getFullYear() - 1;
  const years = [refYear, refYear - 1, refYear - 2, refYear - 3, refYear - 4];

  try {
    const yearAvgs = await Promise.all(
      years.map(y => fetchYearMonthAvg(lat, lng, y, month).catch(() => null))
    );
    const valid = yearAvgs.filter((a): a is number => a !== null);
    const avg = valid.length > 0
      ? Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
      : null;
    climateCache.set(key, avg);
    return avg;
  } catch {
    climateCache.set(key, null);
    return null;
  }
}
